import { useState } from "react";

import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  ArrowPathIcon,
  CheckIcon,
  ChevronDownIcon,
  ClipboardDocumentCheckIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import useStockPermissions from "../../data/useStockPermissions";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";

export type BulkRunTone = "ok" | "held" | "skipped" | "failed";

export interface IBulkRunDetail {
  partId: string;
  label: string;
  text: string;
  tone: BulkRunTone;
}

/** What the last bulk action did, kept on the bar until the next one runs. */
export interface IBulkRunSummary {
  action: string;
  at: string;
  summary: string;
  details: IBulkRunDetail[];
}

export interface IBulkMoreAction {
  key: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: "default" | "danger";
}

export interface IBulkBarBusy {
  pricing: boolean;
  aiTitles: boolean;
  reviewed: boolean;
  relist: boolean;
  qc: boolean;
  scrapOut: boolean;
}

interface IBulkBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  busy: IBulkBarBusy;
  onIntelligentPricing: () => void;
  onAiTitles: () => void;
  onMarkReviewed: () => void;
  onRelist: () => void;
  onSendToQc: () => void;
  onScrapOut: () => void;
  moreActions: IBulkMoreAction[];
  lastRun: IBulkRunSummary | null;
}

const button = (tone: string, extra = "") =>
  classNames(
    "inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-[13px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50",
    tone,
    extra
  );

const TONES = {
  brand: "border-transparent bg-primary-600 text-white hover:bg-primary-700",
  violet: "border-violet-300 bg-white text-violet-700 hover:bg-violet-50",
  sky: "border-sky-300 bg-white text-sky-700 hover:bg-sky-50",
  neutral: "border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
  green: "border-green-200 bg-white text-green-700 hover:bg-green-50",
  indigo: "border-indigo-200 bg-white text-indigo-800 hover:bg-indigo-50",
  red: "border-red-200 bg-white text-red-700 hover:bg-red-50",
};

const DETAIL_TONE_CLASSES: Record<BulkRunTone, string> = {
  ok: "text-green-700",
  held: "text-amber-700",
  skipped: "text-gray-500",
  failed: "text-red-700",
};

const Spinner = () => <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />;

/**
 * The actions that run over the selected rows. Everything here stages or records - except Relist,
 * Send to QC and Scrap out, which act straight away and confirm first in the queue. The last run's
 * outcome stays on the bar so a "24 priced, 3 held" is not lost in a toast.
 */
export default function BulkBar({
  selectedCount,
  onClearSelection,
  busy,
  onIntelligentPricing,
  onAiTitles,
  onMarkReviewed,
  onRelist,
  onSendToQc,
  onScrapOut,
  moreActions,
  lastRun,
}: IBulkBarProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Stock:AdminOnlyChanges - intelligent pricing stages a new price on live listings, admin-only like the API.
  const { restricted } = useStockPermissions();
  const hasSelection = selectedCount > 0;

  if (!hasSelection && !lastRun) return null;

  const anyBusy = Object.values(busy).some(Boolean);

  return (
    <div className="border-b border-primary-200 bg-primary-50" aria-label="Bulk actions">
      {hasSelection && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
          <span className="mr-1 text-[13px] font-semibold text-primary-800">
            {selectedCount} selected
          </span>

          {!restricted && (
            <button type="button" onClick={onIntelligentPricing} disabled={anyBusy} className={button(TONES.brand)}>
              {busy.pricing ? <Spinner /> : <SparklesIcon className="h-4 w-4" aria-hidden="true" />}
              Intelligent pricing + AI review
            </button>
          )}
          <button type="button" onClick={onAiTitles} disabled={anyBusy} className={button(TONES.violet)}>
            {busy.aiTitles ? <Spinner /> : <SparklesIcon className="h-4 w-4" aria-hidden="true" />}
            AI title builder
          </button>
          <button type="button" onClick={onMarkReviewed} disabled={anyBusy} className={button(TONES.neutral)}>
            {busy.reviewed ? <Spinner /> : <CheckIcon className="h-4 w-4" aria-hidden="true" />}
            Mark reviewed
          </button>
          <button type="button" onClick={onRelist} disabled={anyBusy} className={button(TONES.green)}>
            {busy.relist ? <Spinner /> : null}
            Relist
          </button>
          <button type="button" onClick={onSendToQc} disabled={anyBusy} className={button(TONES.indigo)}>
            {busy.qc ? <Spinner /> : <ClipboardDocumentCheckIcon className="h-4 w-4" aria-hidden="true" />}
            Send to QC
          </button>
          <button type="button" onClick={onScrapOut} disabled={anyBusy} className={button(TONES.red)}>
            {busy.scrapOut ? <Spinner /> : null}
            Scrap out
          </button>

          <Menu>
            <MenuButton disabled={anyBusy} className={button(TONES.neutral)}>
              More
              <ChevronDownIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </MenuButton>
            <MenuItems
              anchor={{ to: "bottom start", gap: 4 }}
              className="z-30 w-64 rounded-lg border border-gray-200 bg-white p-1 shadow-xl focus:outline-none"
            >
              {moreActions.map((action) => (
                <MenuItem key={action.key} disabled={action.disabled}>
                  <button
                    type="button"
                    onClick={action.onClick}
                    className={classNames(
                      "block w-full rounded-md px-3 py-2 text-left text-sm data-[focus]:bg-gray-100 data-[disabled]:opacity-50",
                      action.tone === "danger" ? "text-red-700" : "text-gray-700"
                    )}
                  >
                    {action.label}
                  </button>
                </MenuItem>
              ))}
            </MenuItems>
          </Menu>

          <button
            type="button"
            onClick={onClearSelection}
            className="ml-auto text-[13px] font-medium text-primary-700 hover:text-primary-900"
          >
            Clear selection
          </button>
        </div>
      )}

      {lastRun && (
        <div className={classNames("px-4 pb-2 text-xs text-primary-900", hasSelection ? "" : "pt-2")}>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium">
              Last bulk run: {lastRun.summary} · {formatDateTime(lastRun.at, DateTime.TIME_SIMPLE)}
            </span>
            {lastRun.details.length > 0 && (
              <button
                type="button"
                onClick={() => setDetailsOpen((open) => !open)}
                className="font-semibold text-primary-700 hover:text-primary-900"
              >
                {detailsOpen ? "Hide details" : `Details (${lastRun.details.length})`}
              </button>
            )}
          </div>
          {detailsOpen && (
            <ul className="mt-1 max-h-48 space-y-0.5 overflow-y-auto rounded-md border border-primary-100 bg-white p-2">
              {lastRun.details.map((detail) => (
                <li key={`${detail.partId}-${detail.text}`} className="flex gap-2">
                  <span className="w-28 shrink-0 truncate font-medium text-gray-700">{detail.label}</span>
                  <span className={DETAIL_TONE_CLASSES[detail.tone]}>{detail.text}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
